// Pure parts of the Policies side-panel menu (see usePolicyMenu.ts),
// kept free of auth and React so they test under tsx.

import type { AppSidebarItem } from './apps';
import {
  APP_LABEL,
  CATEGORY_ORDER,
  SOURCE_COPY,
  type PolicyAppKey,
  type PolicyInventory,
  type PolicyInventorySource,
  type PolicySourceCopy,
} from '../pages/policiesHub/catalog';

// Sidebar icon keys per rule-set category.
const CATEGORY_ICON: Record<string, string> = {
  detection: 'shield-alert',
  checks: 'clipboard-list',
  exceptions: 'flag',
  responses: 'zap',
  approvals: 'users',
  identity: 'key-round',
  guardrails: 'bot',
};

export const ALL_POLICIES: AppSidebarItem = { to: '/policies', label: 'All policies', iconKey: 'shield', end: true };

/** Short count for a row ("12 on", "On"), from figures the API returned. */
export function menuHint(source: PolicyInventorySource, copy: PolicySourceCopy | undefined): string | undefined {
  const c = source.counts;
  if (copy?.setting && c) return (c.on ?? 0) > 0 ? 'On' : 'Off';
  if (c?.on !== undefined) return `${c.on.toLocaleString('en-US')} on`;
  if (source.total !== undefined && copy?.totalLabel) return source.total.toLocaleString('en-US');
  return undefined;
}

// The menu groups by app (operator review, 23 Oct): the app heading
// carries the context, so names can be short enough for the panel, and
// each row opens a page in that app. The hub keeps the by-category view.
const APP_ORDER: PolicyAppKey[] = ['xes', 'posture', 'agents', 'insider', 'surface', 'resilience', 'organization'];

const GROUP_LABEL: Partial<Record<PolicyAppKey, string>> = { organization: 'Organization-wide' };

// Shorter menu names where the hub's name would be cut off.
const MENU_NAME: Record<string, string> = {
  'xes.sender-rules': 'Sender allow and block list',
  'xes.auto-assign-training': 'Training after blocked email',
  'posture.iac-checks': 'IaC checks',
  'posture.virtualization-checks': 'Virtualization checks',
  'agents.approvals': 'Action approvals',
};

/** Menu rows for an inventory: All policies, then each available rule
 *  set under its app's heading. Tested in policyMenu.test.ts. */
export function policyMenuItems(inventory: PolicyInventory): AppSidebarItem[] {
  const failed = new Set(inventory.failures.map((f) => f.key));
  const rows: AppSidebarItem[] = [ALL_POLICIES];
  const rank = (s: PolicyInventorySource) => CATEGORY_ORDER.indexOf(s.category);
  for (const app of APP_ORDER) {
    const sources = inventory.sources.filter((s) => s.app === app && s.available).sort((a, b) => rank(a) - rank(b));
    for (const source of sources) {
      const copy = SOURCE_COPY[source.key];
      // A rule set whose page lives on another site stays on the hub.
      if (!copy || copy.href.startsWith('external:')) continue;
      const category = source.category;
      rows.push({
        to: copy.href,
        label: MENU_NAME[source.key] ?? copy.name,
        iconKey: CATEGORY_ICON[category] ?? 'shield',
        group: GROUP_LABEL[app] ?? APP_LABEL[app],
        shortcut: true,
        // Approvals already carry the shell's red "waiting" badge.
        ...(failed.has(source.key) || category === 'approvals' ? {} : { hint: menuHint(source, copy) }),
      });
    }
  }
  return rows;
}

